/**
 * Pure kanban helpers.
 *
 * Kept out of the board components so those files export only components and
 * fast refresh keeps working — the same split as `quote-utils.ts` and
 * `invoice-utils.ts`. Nothing here imports React and nothing here fetches.
 *
 * The board contract (see the `Kanban*` types in `types.ts`) is unusually
 * opinionated about three things, and most of the reasoning below is about
 * honouring them rather than fighting them: money is a DECIMAL string, cards
 * come back ordered `id DESC` with no way to ask for anything else, and the
 * column list is a backend registry the frontend must not second-guess.
 */
import { todayIsoDate } from '@/lib/invoice-utils';
import { addDaysIso } from '@/lib/quote-utils';
import type {
  KanbanCard,
  KanbanColumn,
  KanbanCurrencyTotal,
  KanbanDateField,
  KanbanDealColumnMeta,
} from '@/lib/types';

/**
 * Column header for whichever date the board is showing.
 *
 * The board response says which field its cards carry (`meta.dateField`) but
 * not what to call it, and the boards mean genuinely different things by "the
 * date" — a deal is forecasting when it closes, a quote is counting down to
 * lapsing, an invoice is waiting to be paid, a deliverable is running out of
 * time to be delivered. One shared "Date" label would flatten that distinction
 * on exactly the surface where someone is scanning for what needs chasing.
 *
 * A deliverable has both a start and an end date and the backend picked
 * `end_date` for the board, so the label says "End date" rather than a vaguer
 * "Date" or a wrong "Due date": the board is read to see what is running out,
 * and naming the actual column is what makes the card's date checkable against
 * the record.
 */
export const KANBAN_DATE_LABEL: Record<KanbanDateField, string> = {
  closingDate: 'Closing date',
  validTill: 'Valid till',
  dueDate: 'Due date',
  endDate: 'End date',
};

/**
 * Whether a column carries pipeline metadata (probability / won / lost).
 *
 * Tests for the metadata itself rather than `board === 'deals'` on purpose.
 * `extraColumns` is a backend registry field: the server decides which column
 * source projects that metadata, so a fourth board, or a quote stage that
 * grows an `is_won`, starts sending it with no change here. Branching on the
 * board name would silently ignore data the API had already begun returning.
 *
 * `probability` is the discriminator because it is the one member the contract
 * documents as never null when present — a NULL in the master surfaces as `0`
 * — so `typeof === 'number'` is a true presence test. `isWon`/`isLost` are
 * booleans, and a truthiness check on either would read a legitimate `false`
 * as "this column has no metadata".
 */
export function isDealColumn(
  column: KanbanColumn,
): column is KanbanColumn & KanbanDealColumnMeta {
  return typeof column.probability === 'number';
}

/**
 * A stable React key for a column.
 *
 * The "Unassigned" bucket has `id: null`, and letting `String(null)` produce
 * `"null"` reads as a bug the first time someone greps for it. Naming it
 * explicitly also keeps the key stable across refetches: Unassigned is a
 * synthesised column with no identity of its own to fall back on.
 */
export function columnKeyOf(column: KanbanColumn): string {
  return column.id === null ? 'unassigned' : String(column.id);
}

/**
 * Whether a card may be dropped INTO this column.
 *
 * Two separate reasons, both real, and neither redundant:
 *
 * 1. A retired column (`isActive: false`) is drag-OUT only. Its cards are
 *    still there and still need moving on, but the move endpoint answers a
 *    move *into* one with a 422 — "... is inactive and cannot be moved into".
 *    Testing it here turns a server error into a cursor that never offers the
 *    drop in the first place.
 * 2. "Unassigned" (`id: null`) is not merely refused, it is INEXPRESSIBLE. The
 *    move body takes a single positive integer under `data.columnKey`; there
 *    is no null, no sentinel and no "clear the stage" verb, so no request
 *    exists that means "put this card back into Unassigned". Cards leave that
 *    column and never return by drag.
 */
export function isDropTarget(column: KanbanColumn): boolean {
  return column.isActive && column.id !== null;
}

/**
 * Places a card into a column's visible list at its `id DESC` position.
 *
 * The server orders cards by id descending and that is not negotiable: none of
 * the three sources has a position column, and the endpoint takes no sort
 * parameter. So an optimistic drop that appends (or unshifts) puts the card
 * somewhere the next refetch will not, and it visibly jumps once the response
 * lands. Inserting at the id-DESC position makes the optimistic list identical
 * to what the server will return.
 *
 * Any existing copy is removed first, so a repeated drop — or a drop back into
 * the column the card came from — cannot duplicate it.
 */
export function insertByIdDesc(cards: KanbanCard[], card: KanbanCard): KanbanCard[] {
  const without = cards.filter((existing) => existing.id !== card.id);
  const at = without.findIndex((existing) => existing.id < card.id);
  if (at === -1) return [...without, card];
  return [...without.slice(0, at), card, ...without.slice(at)];
}

/** Money is carried at two decimal places end to end; so is the arithmetic. */
const MONEY_SCALE = 2;

interface Scaled {
  /** The value with its decimal point removed. */
  units: bigint;
  /** How many digits were after the point. */
  scale: number;
}

/** Splits a decimal string into integer units and the scale they are held at. */
function parseScaled(value: string): Scaled | null {
  const trimmed = value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;

  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = unsigned.split('.');
  const units = BigInt(whole + fraction);
  return { units: negative ? -units : units, scale: fraction.length };
}

/** Restates a parsed value in whole cents, rounding half away from zero. */
function toMoneyUnits({ units, scale }: Scaled): bigint {
  if (scale === MONEY_SCALE) return units;
  if (scale < MONEY_SCALE) return units * 10n ** BigInt(MONEY_SCALE - scale);

  const divisor = 10n ** BigInt(scale - MONEY_SCALE);
  const negative = units < 0n;
  const magnitude = negative ? -units : units;
  const quotient = (magnitude * 2n + divisor) / (divisor * 2n);
  return negative ? -quotient : quotient;
}

/** Renders whole cents back to a fixed 2dp string. */
function formatMoneyUnits(units: bigint): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(MONEY_SCALE + 1, '0');
  const whole = digits.slice(0, digits.length - MONEY_SCALE);
  const fraction = digits.slice(digits.length - MONEY_SCALE);
  return negative ? `-${whole}.${fraction}` : `${whole}.${fraction}`;
}

/**
 * Shifts a column's `totalAmount` by one card's amount as it moves in or out.
 *
 * `direction` is `1` when the card lands in this column and `-1` when it
 * leaves, so one drag calls this twice — once per column — and both headers
 * settle before the refetch confirms them.
 *
 * MONEY IS NEVER A FLOAT. `Number("18000.10") * 100` is not `1801000`, and a
 * column header maintained that way drifts a cent per drag until it disagrees
 * with the figure the API reports. Everything below is scaled `BigInt`, in the
 * same style as `decimal.ts`.
 *
 * It deliberately does not reuse `sumDecimalStrings`. That function only adds,
 * and there is no negated decimal string to hand it (prefixing `-` by hand is
 * wrong the moment the amount is itself negative, which a credit note makes
 * real); and it preserves the widest input scale, whereas a column total has
 * to stay at the fixed 2dp the API renders — otherwise the header changes
 * shape mid-drag and then changes back on refetch.
 *
 * A `null` amount is a card with no money on it: a no-op, not a zero. Anything
 * unparseable returns `total` untouched — a briefly stale header is fixed by
 * the next refetch, whereas `NaN` in a money column is not recoverable and
 * gets screenshotted.
 *
 * A `null` TOTAL is the same refusal one level up, and it is why the return
 * type is nullable rather than the null being defaulted away at the boundary.
 * `totalAmount: null` means the board carries no money at all — a deliverable
 * is a unit of work whose value lives on the quote or invoice raised against
 * it — so there is nothing to shift and no figure this function is entitled to
 * invent. Seeding it with `'0.00'` and adding to that would manufacture a total
 * the server never reported, out of a drag, on a board that has none: the
 * column would start claiming money the moment someone moved a card. Null in,
 * null out, whichever direction the card is going.
 */
export function adjustColumnTotal(
  total: string | null,
  amount: string | null,
  direction: 1 | -1,
): string | null {
  if (total === null) return null;
  if (amount === null) return total;

  const parsedTotal = parseScaled(total);
  const parsedAmount = parseScaled(amount);
  if (parsedTotal === null || parsedAmount === null) return total;

  const shifted = toMoneyUnits(parsedTotal) + BigInt(direction) * toMoneyUnits(parsedAmount);
  return formatMoneyUnits(shifted);
}

/** Currency ascending, the `null` currency last — the order the server sends. */
function compareCurrency(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a < b ? -1 : 1;
}

/**
 * Shifts a column's `totalsByCurrency` by one card as it moves in or out —
 * the per-currency twin of `adjustColumnTotal`, with the same scaled-BigInt
 * arithmetic and the same refusals.
 *
 * `undefined` (an older API) and `null` (a board with no money) come straight
 * back: there is nothing to shift and nothing this function may invent. A card
 * with a `null` or unparseable amount is a no-op, and so is a card leaving a
 * column whose breakdown has no entry for its currency — a negative entry
 * would be a figure the server never reported.
 *
 * Landing adds to the card's currency entry, or inserts one at the server's
 * sort position. Leaving subtracts, and DROPS the entry only when the server
 * would: it reports only currencies present in the filtered column, so an
 * entry goes once its last card has left. That is only knowable when the
 * column is fully loaded (`hasMore` false) — `column` is the column AFTER the
 * move. When cards are hidden behind `hasMore`, or a visible card still
 * carries the currency, the entry is kept (possibly at `0.00`) rather than
 * guessed away; the next refetch settles it.
 */
export function adjustCurrencyTotals(
  totals: KanbanCurrencyTotal[] | null | undefined,
  card: KanbanCard,
  direction: 1 | -1,
  column: Pick<KanbanColumn, 'cards' | 'hasMore'>,
): KanbanCurrencyTotal[] | null | undefined {
  if (totals === undefined || totals === null) return totals;
  if (card.amount === null) return totals;

  const parsedAmount = parseScaled(card.amount);
  if (parsedAmount === null) return totals;
  const delta = BigInt(direction) * toMoneyUnits(parsedAmount);

  const index = totals.findIndex((entry) => entry.currency === card.currency);

  if (index === -1) {
    if (direction === -1) return totals;
    const entry: KanbanCurrencyTotal = { currency: card.currency, amount: formatMoneyUnits(delta) };
    const at = totals.findIndex((existing) => compareCurrency(existing.currency, card.currency) > 0);
    return at === -1
      ? [...totals, entry]
      : [...totals.slice(0, at), entry, ...totals.slice(at)];
  }

  const existing = totals[index];
  if (existing === undefined) return totals;
  const parsedTotal = parseScaled(existing.amount);
  if (parsedTotal === null) return totals;

  if (direction === -1) {
    const lastOfCurrency =
      !column.hasMore && !column.cards.some((other) => other.currency === card.currency);
    if (lastOfCurrency) return totals.filter((_, i) => i !== index);
  }

  const shifted: KanbanCurrencyTotal = {
    currency: existing.currency,
    amount: formatMoneyUnits(toMoneyUnits(parsedTotal) + delta),
  };
  return totals.map((entry, i) => (i === index ? shifted : entry));
}

/**
 * The distinct currency codes present on the cards a column is showing.
 *
 * First-seen order rather than sorted: the list sits beside a total that is
 * read top-down with the cards, and "the order you meet them" does not
 * reshuffle when a single card moves in or out.
 *
 * Only the VISIBLE cards, deliberately. A column is capped at
 * `meta.cardsPerColumn` and `hasMore` says the rest exist, so this can never
 * honestly claim to be the column's full currency set — it is a caveat about
 * the figure on screen, and there is no "load more" endpoint to make it more
 * than that.
 */
export function visibleCurrencies(column: KanbanColumn): string[] {
  const seen: string[] = [];
  for (const card of column.cards) {
    if (card.currency !== null && !seen.includes(card.currency)) seen.push(card.currency);
  }
  return seen;
}

/**
 * Whether a column's total is adding up more than one currency.
 *
 * `totalAmount` sums ACROSS currencies — the contract says so plainly. When
 * this is true the header figure is arithmetically real but financially
 * meaningless, and the UI owes the reader a warning rather than a confident
 * number.
 *
 * Only consulted on the FALLBACK path now: a column that carries
 * `totalsByCurrency` (2026-10-15) is rendered one figure per currency and never
 * needs this warning.
 */
export function isMixedCurrency(column: KanbanColumn): boolean {
  return visibleCurrencies(column).length > 1;
}

export type KanbanDateTone = 'overdue' | 'due-soon' | 'normal';

/** How far ahead still counts as "coming up", inclusive of the last day. */
const DUE_SOON_DAYS = 7;

/**
 * How urgent a card's date is, relative to the VIEWER's today.
 *
 * A pure lexicographic comparison of bare 'YYYY-MM-DD' strings, which sort
 * correctly as text and carry no timezone at all. Never build a `Date` from
 * one: `new Date('2026-08-24')` is parsed as UTC midnight and read back
 * locally, so it lands a day early for every viewer west of Greenwich — and
 * `toISOString().slice(0, 10)` is the same bug mirrored, handing back
 * tomorrow's date to anyone east of Greenwich in their local evening and so
 * flagging a card overdue a day early. `todayIsoDate` assembles today from
 * local calendar parts precisely to avoid both, and `addDaysIso` uses a `Date`
 * only as a calendar calculator, constructed from and read back through local
 * components. `quote-utils.ts` already compares expiry this way; importing
 * both rather than restating them keeps one implementation to get right.
 *
 * Urgency is computed here and never on the server: a boolean baked into a
 * response is already answering the wrong question for a viewer in another
 * timezone, and goes stale the moment their midnight passes.
 *
 * The tone applies to all three boards. `_field` is accepted so a call site
 * reads as "the tone of this date, which is the closing date", and so a board
 * can diverge later — but today a closing date, a quote expiry and an invoice
 * due date all say the same thing to someone scanning the board: this needs
 * attention. A `null` date is absence, not urgency, so it gets no tone at all
 * rather than a reassuring 'normal'.
 */
export function dateTone(
  date: string | null,
  _field: KanbanDateField,
): KanbanDateTone | null {
  if (date === null) return null;

  const today = todayIsoDate();
  if (date < today) return 'overdue';
  return date <= addDaysIso(today, DUE_SOON_DAYS) ? 'due-soon' : 'normal';
}
